/**
 * Select — the §7.2 popover select, on `@radix-ui/react-select` (mission 6
 * WP03 T021; FR-005, FR-027; DM-01M3C4XR0TYMTDR6BNEBZBQGXF).
 *
 * A 36px trigger with the input border (navy-500) and a 16px navy-600
 * chevron; a popper menu with `--shadow-sm`, radius-md and a navy-200 border;
 * 36px items, highlighted navy-100, checked accent-50 with a Sky-burst check
 * stroke (a stroke, never a Sky-burst text colour). Keyboard: Enter, Space or the arrows open
 * it; arrows move; Enter selects.
 *
 * jsdom needs `hasPointerCapture`, `releasePointerCapture`, `scrollIntoView`
 * and `ResizeObserver` stubbed to drive it (see ui.test.tsx and the README).
 */
import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'

import { cn } from './cn'

export const Select = SelectPrimitive.Root
export const SelectGroup = SelectPrimitive.Group
export const SelectValue = SelectPrimitive.Value

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-navy-500 bg-white px-3 text-left text-body text-navy-950 transition-colors duration-[var(--duration-fast)]',
      'hover:border-navy-600 focus-visible:border-brand-sky focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]',
      'data-[placeholder]:text-navy-600 aria-invalid:border-[var(--color-error-600)]',
      'disabled:cursor-not-allowed disabled:border-navy-400 disabled:bg-navy-100 disabled:text-navy-600',
      className,
    )}
    {...props}
  >
    <span className="min-w-0 truncate">{children}</span>
    <SelectPrimitive.Icon asChild>
      <ChevronDown aria-hidden className="size-4 shrink-0 text-navy-600" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = 'SelectTrigger'

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', sideOffset = 4, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      sideOffset={sideOffset}
      className={cn(
        'z-50 max-h-80 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-navy-200 bg-white text-navy-950 [box-shadow:var(--shadow-sm)]',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = 'SelectContent'

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex h-9 cursor-default select-none items-center rounded-sm pl-3 pr-8 text-body text-navy-950 outline-none',
      'data-[highlighted]:bg-navy-100 data-[state=checked]:bg-[var(--color-accent-50)] data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="absolute right-2 inline-flex items-center">
      <Check aria-hidden className="size-4 stroke-brand-sky" />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
))
SelectItem.displayName = 'SelectItem'
